import React, { useRef, useState, useEffect } from "react";
import style from "../Information/style.module.css";
import Button from "../button/Button";
import { IoAdd, IoClose } from "react-icons/io5";
import { MdOutlineSubtitles, MdOutlineAudiotrack } from "react-icons/md";
import MediaLibraryModal from "../mediaLibrary/MediaLibraryModal";
import Accordion from "../../Accordian/Accordian";
import Advanced from "../mp4FileUpload/Advanced";
import { showToast } from "../../utils/Toastify/Toastify";
import AiGeneratedBadge from "../aiAssist/AiGeneratedBadge";
import useAiSubtitles from "../../hooks/useAiSubtitles";
import useAiDubbing from "../../hooks/useAiDubbing";
import useAiThumbnails from "../../hooks/useAiThumbnails";
import { useAiEnabled } from "../../context/AppConfigContext";

// Shared with the AI Studio so both paths merge results by the same rules — an
// explicit generate REPLACES an existing language instead of silently discarding the
// new file. See applyAiResults.js for why.
import {
  subtitleResultToFiles,
  mergeSubtitleFiles,
  dropSubtitleLanguages,
  dubResultToTracks,
  mergeAudioTracks,
  describeMerge,
} from "../../services/ai/applyAiResults";
import { detectSubtitleLanguage } from "../../utils/subtitleLanguage";

// Languages offered as AI translation targets (the original spoken language is
// always transcribed regardless). Codes match the backend subtitleLanguageLabel map.
const SUBTITLE_TARGET_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "hi", name: "Hindi" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "ar", name: "Arabic" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "bn", name: "Bengali" },
  { code: "ml", name: "Malayalam" },
  { code: "pt", name: "Portuguese" },
  { code: "zh", name: "Chinese" },
  { code: "ja", name: "Japanese" },
  { code: "ru", name: "Russian" },
];

const getHash = () => {
  try {
    return typeof window !== "undefined" ? window.location.hash : "";
  } catch {
    return "";
  }
};

const MediaAssets = ({
  formData,
  handleChange,
  handleIconClick,
  isLandscapeUploaded,
  isPortraitUploaded,
  bannerPreviewURL,
  imagePreviewURL,
  isOpen,
  setIsOpen,
  setSelectedVideos,
  handleimagesvalue,
  setIsPortraitUploaded,
  selectedImage,
  setSelectedImage,
  portraitInputRef,
  handleAdvanced,
  showAdvanced,
  movieData,
  handleCastCrewChange,
  selectedVideos,
  setSelectedVideos: setSelectedVideosAlias,
  setTrailer,
  setVideo,
  video,
  name,
  soloProps,
  onSubtitleChange,
  /**
   * Subtitles produced by the AI Studio, as `{ files, token }`.
   *
   * Delivered as a prop because THIS component owns the subtitle list — the parent only
   * receives it through `onSubtitleChange`. The Studio therefore can't write the list
   * itself; it hands the files here and this component merges them, so generated
   * captions appear in the panel and survive the next `onSubtitleChange`.
   */
  aiSubtitleDrop,
  onLogoChange,
  heroPreviewURL,
  logoPreviewURL,
  onHeroFileChange,
  onVideoFileChange,
  onLogoRemove,
  onHeroRemove,
  audioTracks = [],
  onAudioTracksChange,
  onThumbnailsChange,
  thumbnailsVtt,
  aiFields,
}) => {
  // True when the given asset slot currently holds an AI-generated value.
  const isAi = (key) => Boolean(aiFields && aiFields.has && aiFields.has(key));
  // AI master switch, from GET /config (CMS Settings page).
  const aiEnabled = useAiEnabled();
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
  const [subtitleFiles, setSubtitleFiles] = useState(Array(10).fill(null));
  // Latest list for async handlers (handleSubtitleFile awaits before updating),
  // so two uploads finishing back to back don't drop each other.
  const subtitleFilesRef = useRef(subtitleFiles);
  subtitleFilesRef.current = subtitleFiles;
  const [prefillSubtitles, setPrefillSubtitles] = useState([]);
  const prefillSubtitlesRef = useRef(prefillSubtitles);
  prefillSubtitlesRef.current = prefillSubtitles;
  // Guard: initialize prefill from server data ONLY ONCE. Without this guard, any
  // unrelated re-render that mutates `movieData` will overwrite the user's local
  // edits (e.g. resurrect a subtitle row the user just removed).
  const prefillInitializedRef = useRef(false);
  const subtitleRefs = useRef({});

  const getSubtitleRef = (idx) => {
    if (!subtitleRefs.current[idx]) subtitleRefs.current[idx] = React.createRef();
    return subtitleRefs.current[idx];
  };

  // ── AI subtitle generation ──
  const aiSubs = useAiSubtitles();
  const [subtitleTargets, setSubtitleTargets] = useState([]); // extra translation languages
  const aiSubsAppliedRef = useRef(null); // guards single-apply per completed result

  // The video the AI should transcribe: prefer the selected CDN/library video
  // (same source the trailer generator uses), falling back to the raw file.
  const aiSubtitleSource =
    video ||
    (Array.isArray(selectedVideos) ? selectedVideos[0] : selectedVideos) ||
    null;

  const toggleSubtitleTarget = (code) => {
    setSubtitleTargets((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // Display name for a language code (falls back to the uppercased code).
  const langName = (code) => {
    const c = String(code || "").toLowerCase();
    return (
      SUBTITLE_TARGET_LANGUAGES.find((l) => l.code === c)?.name ||
      c.toUpperCase()
    );
  };

  // The set of subtitle languages that ALREADY exist (prefilled from the server
  // or freshly added/generated in this session). Used to mark the language chips
  // as "done" so a returning user instantly sees what they already have, and to
  // avoid regenerating a language that's already present.
  const existingSubtitleLangs = React.useMemo(() => {
    const set = new Set();
    (prefillSubtitles || []).forEach((s) => {
      if (s?.language) set.add(String(s.language).toLowerCase());
    });
    (subtitleFiles || []).forEach((f) => {
      if (f?.language) set.add(String(f.language).toLowerCase());
    });
    return set;
  }, [prefillSubtitles, subtitleFiles]);

  // A single, unified list of subtitle cards to render — existing (server) rows
  // and locally-added files merged, each normalized to one shape. This replaces
  // the old "prefill grid + 10 fixed upload slots" with a clean, dynamic grid.
  const subtitleCards = React.useMemo(() => {
    const cards = [];
    (prefillSubtitles || []).forEach((s, index) => {
      cards.push({
        key: `prefill-${s.id ?? index}`,
        kind: "existing",
        prefillIndex: index,
        language: s.language,
        format: s.format,
        url: s.subtitle_url,
      });
    });
    (subtitleFiles || []).forEach((f, index) => {
      if (!f) return;
      cards.push({
        key: `file-${index}`,
        kind: "file",
        fileIndex: index,
        language: f.language || null,
        format: f.format || (f.name?.endsWith(".vtt") ? "vtt" : "srt"),
        name: f.name,
        aiGenerated: Boolean(f.aiGenerated),
        translated: Boolean(f.translated),
      });
    });
    return cards;
  }, [prefillSubtitles, subtitleFiles]);

  // Open the next empty upload slot's file dialog (for the single "+ Add" tile).
  const openNextSubtitleUpload = () => {
    const nextEmpty = subtitleFiles.findIndex((f) => !f);
    const idx = nextEmpty === -1 ? subtitleFiles.length - 1 : nextEmpty;
    const input = document.getElementById(`subtitle-input-${idx}`);
    if (input) input.click();
  };

  const handleGenerateSubtitles = () => {
    if (!aiSubtitleSource) {
      showToast("Select or upload a video first, then generate subtitles.");
      return;
    }
    aiSubsAppliedRef.current = null;
    setSubtitlesEnabled(true);
    aiSubs.generate(aiSubtitleSource, { targetLanguages: subtitleTargets });
  };

  // When a generation completes, fetch each produced .srt into a real File (with its
  // language/format attached) and merge it in — this reuses the exact same
  // upload-on-save path as a manually-picked .srt file.
  //
  // An explicit "Generate with AI" REPLACES a language that already exists rather
  // than skipping it. Skipping was a real bug: the server produced a correct, full
  // subtitle and the CMS silently threw it away in favour of the stale one, so
  // pressing the button after a fix appeared to do nothing at all. The operator asked
  // for this language, so the new output is the one they want.
  //
  // Replacing a SERVER row means dropping it from `prefillSubtitles` as well, which is
  // what removes its id from `subtitles_keep_ids` on save — the backend then deletes
  // the old subtitle and stores the new upload in its place.
  useEffect(() => {
    if (!aiSubs.isComplete || !aiSubs.result) return;
    if (aiSubsAppliedRef.current === aiSubs.result) return; // already applied
    aiSubsAppliedRef.current = aiSubs.result;

    let cancelled = false;
    (async () => {
      const { files, failed } = await subtitleResultToFiles(aiSubs.result);
      if (cancelled) return;

      if (files.length === 0) {
        showToast(
          failed.length
            ? "Subtitles were generated but could not be loaded. Try again."
            : "No subtitles were produced for this video."
        );
        return;
      }

      const generatedLangs = files.map((f) => f.language);
      const { kept: keptPrefill, removed } = dropSubtitleLanguages(
        prefillSubtitles,
        generatedLangs
      );
      const { next, replaced, added } = mergeSubtitleFiles(subtitleFiles, files);

      if (removed.length > 0) setPrefillSubtitles(keptPrefill);
      setSubtitleFiles(next);
      if (onSubtitleChange) onSubtitleChange(buildCombinedSubtitles(keptPrefill, next));

      const detail = describeMerge(
        { replaced: [...replaced, ...removed.map((r) => r.language)], added },
        langName
      );
      const warnings = aiSubs.result.warnings || [];
      showToast(
        `Subtitles ready — ${detail}${warnings.length ? ` · ${warnings.length} note(s)` : ""}. Review and Save.`
      );
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSubs.isComplete, aiSubs.result]);

  // Absorb subtitles generated by the AI Studio. Same merge rules as the panel's own
  // "Generate with AI" button — an explicit generate REPLACES an existing language — and
  // it calls onSubtitleChange itself so the parent's save payload stays in step.
  const aiDropAppliedRef = useRef(null);
  useEffect(() => {
    const token = aiSubtitleDrop?.token;
    const files = aiSubtitleDrop?.files;
    if (!token || !files?.length) return;
    if (aiDropAppliedRef.current === token) return; // already absorbed
    aiDropAppliedRef.current = token;

    const generatedLangs = files.map((f) => f.language);
    const { kept: keptPrefill, removed } = dropSubtitleLanguages(
      prefillSubtitles,
      generatedLangs
    );
    const { next, replaced, added } = mergeSubtitleFiles(subtitleFiles, files);

    if (removed.length > 0) setPrefillSubtitles(keptPrefill);
    setSubtitleFiles(next);
    setSubtitlesEnabled(true); // reveal the section so the new cards are visible
    if (onSubtitleChange) onSubtitleChange(buildCombinedSubtitles(keptPrefill, next));

    const detail = describeMerge(
      { replaced: [...replaced, ...removed.map((r) => r.language)], added },
      langName
    );
    showToast(`Subtitles ready — ${detail}. Review and Save.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSubtitleDrop?.token]);

  // ── AI dubbing (generate spoken audio tracks per language) ──
  const aiDub = useAiDubbing();
  const [dubTargets, setDubTargets] = useState([]);
  const aiDubAppliedRef = useRef(null);

  const toggleDubTarget = (code) => {
    setDubTargets((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleGenerateDub = () => {
    if (!aiSubtitleSource) {
      showToast("Select or upload a video first, then generate dubbed audio.");
      return;
    }
    if (dubTargets.length === 0) {
      showToast("Pick at least one language to dub into.");
      return;
    }
    // Dub exactly what the operator selected — including languages that already have
    // a track, which are REPLACED. Previously these were dropped here at request time
    // to save TTS quota, but that made "regenerate this dub" impossible: the button
    // reported "already dubbed" and nothing happened, even when the existing track was
    // the broken one being fixed. The chips already mark existing languages with a ✓,
    // so selecting one is a deliberate choice to redo it.
    const existingLangs = new Set(
      (audioTracks || []).map((t) =>
        String(t.languageCode || t.language || "").toLowerCase()
      )
    );
    const redoing = dubTargets.filter((c) => existingLangs.has(String(c).toLowerCase()));
    if (redoing.length > 0) {
      showToast(
        `Replacing existing dub for ${redoing.map((c) => langName(c)).join(", ")}.`
      );
    }
    aiDubAppliedRef.current = null;
    aiDub.generate(aiSubtitleSource, { targetLanguages: dubTargets });
  };

  // When a dub completes, FETCH each generated .m4a into a real File — exactly
  // like AI subtitles do with their .srt — and attach that File on the audio-track
  // row. On Save the File is uploaded to Bunny through the same path as a subtitle
  // file (see Mp4FileUpload), so dubbing follows the identical caption process.
  // The blob URL is kept only for the in-CMS <audio> preview.
  useEffect(() => {
    if (!aiDub.isComplete || !aiDub.result) return;
    if (aiDubAppliedRef.current === aiDub.result) return;
    aiDubAppliedRef.current = aiDub.result;

    const generated = aiDub.result.audioTracks || [];
    if (generated.length === 0) return;

    let cancelled = false;
    (async () => {
      const { tracks, failed } = await dubResultToTracks(aiDub.result, langName);
      if (cancelled) return;

      if (tracks.length === 0) {
        showToast(
          failed.length
            ? "Dub was generated but could not be loaded. Try again."
            : "No dubbed audio was produced for this video."
        );
        return;
      }

      // Replace a language that already has a track rather than stacking a second one
      // beside it — two tracks for the same language would both show up in the
      // player's audio menu, and the stale one might win.
      const { next, replaced, added } = mergeAudioTracks(audioTracks, tracks);
      onAudioTracksChange?.(next);

      const detail = describeMerge({ replaced, added }, langName);
      const warnings = aiDub.result.warnings || [];
      showToast(
        `Dubbed audio ready — ${detail}${warnings.length ? ` · ${warnings.length} note(s)` : ""}. Preview below, then Save.`
      );
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDub.isComplete, aiDub.result]);

  // ── AI seek-bar preview thumbnails (trickplay) ──
  const aiThumbs = useAiThumbnails();
  const aiThumbsAppliedRef = useRef(null);
  // How often to sample a frame. Lower = smoother scrubbing but a bigger sprite +
  // longer generation. 10s is the default (industry norm for long content); the
  // operator can pick finer for short clips.
  const [thumbInterval, setThumbInterval] = useState(10);

  const handleGenerateThumbnails = () => {
    if (!aiSubtitleSource) {
      showToast("Select or upload a video first, then generate scrubbing previews.");
      return;
    }
    aiThumbsAppliedRef.current = null;
    aiThumbs.generate(aiSubtitleSource, { interval: thumbInterval });
  };

  // When thumbnails complete, hand the parent the VTT (+ sprite) URLs so they're
  // saved onto the content's other_info. Unlike subtitles/dub there's no File to
  // upload — the sprite + .vtt already live on the analyze server and are served
  // by URL, so we just persist those URLs.
  useEffect(() => {
    if (!aiThumbs.isComplete || !aiThumbs.result) return;
    if (aiThumbsAppliedRef.current === aiThumbs.result) return;
    aiThumbsAppliedRef.current = aiThumbs.result;

    const { vttUrl, spriteUrl, frameCount, interval } = aiThumbs.result;
    if (!vttUrl) return;
    onThumbnailsChange?.({ vttUrl, spriteUrl, frameCount, interval });
    showToast(
      `Scrubbing previews ready — ${frameCount} frame(s) every ${interval}s. Save to apply.`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiThumbs.isComplete, aiThumbs.result]);

  // Build the canonical list the parent expects: existing (prefilled) rows tagged
  // `isExisting:true` with their DB id, followed by any new File picks. The parent
  // splits these into `subtitles_keep_ids` (existing) and uploads (new files).
  const buildCombinedSubtitles = (prefill = prefillSubtitles, files = subtitleFiles) => [
    ...(prefill || []).map((s) => ({
      isExisting: true,
      id: s.id,
      name: `${s.language}.${s.format}`,
      url: s.subtitle_url,
      language: s.language,
      format: s.format,
    })),
    ...(files || []).filter(Boolean),
  ];

  const removeSubtitleFile = (index) => {
    const updated = [...subtitleFiles];
    updated[index] = null;
    setSubtitleFiles(updated);
    const input = document.getElementById(`subtitle-input-${index}`);
    if (input) input.value = "";

    if (onSubtitleChange) onSubtitleChange(buildCombinedSubtitles(prefillSubtitles, updated));
  };

  const [heroLocalPreview, setHeroLocalPreview] = useState(null);
  const [videoLocalPreview, setVideoLocalPreview] = useState(null);
  const [heroFileName, setHeroFileName] = useState(null);
  const [videoFileName, setVideoFileName] = useState(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [heroRemoved, setHeroRemoved] = useState(false);
  const heroFileRef = useRef();
  const videoFileRef = useRef();
  const hash = getHash();
  const [modalMediaType, setModalMediaType] = useState("image");

  const isLive =
    formData?.["content-type"] === "Live Channel" ||
    formData?.["content-type"] === "Live Event";

  const isLiveEvent = formData?.["content-type"] === "Live Event";

  const isSeries =
    formData?.["content-type"] === "Series" ||
    hash.includes("/Addseries");
  const isSeason = hash.includes("/add");
  const isEpisodeAdd = hash.split("/").length >= 5 && hash.includes("/video/");

  // ── Live stream — loop toggle only (§5) ──
  // "loop" defaults to true for Live content, the rest of the source-type /
  // preview UI has been removed in favor of reusing the standard Main Video
  // upload cell + URL field below.
  useEffect(() => {
    const pickedId = video?.id ?? video?._id;
    if (isLive && pickedId && !videoLocalPreview && formData?.loop === undefined) {
      handleChange({ target: { name: "loop", value: true } });
    }
  }, [isLive, video, videoLocalPreview, formData?.loop]);

  // Reset removed flags when parent provides a new value
  useEffect(() => {
    if (logoPreviewURL) setLogoRemoved(false);
  }, [logoPreviewURL]);

  useEffect(() => {
    if (heroPreviewURL) setHeroRemoved(false);
  }, [heroPreviewURL]);

  // NOTE: Live content is never actually played in the Main Video preview cell
  // (see `showMainVideoSection` / the isLive thumbnail branch below) — we only
  // ever show a static thumbnail there, so there's no hls.js manifest attachment
  // needed anymore (the old local HLSVideoPlayer component was removed).

  useEffect(() => {
    if (prefillInitializedRef.current) return;
    if (Array.isArray(movieData?.subtitles) && movieData.subtitles.length > 0) {
      prefillInitializedRef.current = true;
      setPrefillSubtitles(movieData.subtitles);
      setSubtitlesEnabled(true);
      // Push the prefilled (existing) rows to the parent right away so a plain
      // Save (no subtitle edits) still sends them in `subtitles_keep_ids`.
      if (onSubtitleChange) {
        onSubtitleChange(buildCombinedSubtitles(movieData.subtitles, subtitleFiles));
      }
    }
  }, [movieData]);

  // NOTE: existing audio tracks are prefilled on edit by the PARENT
  // (Mp4FileUpload) via the `audioTracks` prop — same as subtitles. We deliberately
  // do NOT prefill again here to avoid double-seeding the list.

  // Keep the URL field in sync with whichever video is actually selected.
  // We track the *identity* of the picked video (not whether the url field is
  // empty) — otherwise, once a URL is set, picking a DIFFERENT video from the
  // Media Library would never update it again.
  const prevVideoIdRef = useRef();
  useEffect(() => {
    const currentId =
      video?.id ??
      video?._id ??
      video?.HLSPlaylistURL ??
      video?.preview_url;

    if (!currentId || prevVideoIdRef.current === currentId) return;

    prevVideoIdRef.current = currentId;

    // Store the selected media id
    if (video?.id || video?._id) {
      handleChange({
        target: {
          name: "media_video_id",
          value: video?.id ?? video?._id,
        },
      });
    }

    // Keep URL only for preview/display if required
    const resolvedVideoUrl =
      video?.HLSPlaylistURL || video?.preview_url;

    if (resolvedVideoUrl) {
      handleChange({
        target: {
          name: "url",
          value: resolvedVideoUrl,
        },
      });
    }
  }, [video]);

  const handleSubtitleFile = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".srt")) {
      showToast("Only .srt subtitle files are allowed. Please select a valid .srt file.");
      e.target.value = "";
      return;
    }

    // Label the file by the language in its name (e.g. "movie.es.srt",
    // "Japanese.srt"; falls back to its text) instead of its slot position.
    // Carried on the File itself, like AI-generated subtitles, so the save
    // payload (Mp4FileUpload) picks it up.
    const detected = await detectSubtitleLanguage(file);

    // Read state AFTER the await — another pick/removal may have landed meanwhile.
    const latestFiles = subtitleFilesRef.current;
    const latestPrefill = prefillSubtitlesRef.current;

    // Languages already used by an existing (server) subtitle or another slot.
    const taken = new Set();
    (latestPrefill || []).forEach((sub) => {
      if (sub?.language) taken.add(String(sub.language).toLowerCase());
    });
    (latestFiles || []).forEach((f, i) => {
      if (i !== index && f?.language) taken.add(String(f.language).toLowerCase());
    });

    let language = detected ? String(detected).toLowerCase() : null;
    if (language && taken.has(language)) {
      // Don't save two subtitles under one language — fall back to the old
      // slot defaults (en, then hi), else "other", and tell the user.
      const fallback = ["en", "hi"].find((l) => !taken.has(l)) || "other";
      showToast(
        `A ${language.toUpperCase()} subtitle is already added — saving "${file.name}" as ${fallback.toUpperCase()} instead.`
      );
      language = fallback;
    }
    if (language) file.language = language;

    const updated = [...latestFiles];
    updated[index] = file;
    subtitleFilesRef.current = updated;
    setSubtitleFiles(updated);
    if (onSubtitleChange) onSubtitleChange(buildCombinedSubtitles(latestPrefill, updated));
  };

  const handleHeroFilePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const localURL = URL.createObjectURL(file);
    setHeroLocalPreview(localURL);
    setHeroFileName(file.name);
    if (onHeroFileChange) onHeroFileChange(file);
  };

  const handleVideoFilePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const localURL = URL.createObjectURL(file);
    setVideoLocalPreview(localURL);
    setVideoFileName(file.name);
    if (onVideoFileChange) onVideoFileChange(file);
    handleChange({ target: { name: "url", value: file.name } });
  };

  // Clearing the URL field should also drop whatever main video was picked —
  // whether that's a directly uploaded file (videoLocalPreview) or a video
  // selected from the Media Library (the `video` prop) — otherwise the
  // preview box keeps showing a video the URL no longer points to.
  // Bunny CDN HLS URLs look like https://vz-xxx.b-cdn.net/{video-guid}/playlist.m3u8
  // — pull the GUID segment out so `video.id` (and therefore `video_id` in the
  // save payload) is a real id, not the whole manifest URL.
  const extractVideoIdFromUrl = (url) => {
    if (!url) return null;
    const match = url.match(/\/([^\/]+)\/playlist\.m3u8(?:\?.*)?$/i);
    return match ? match[1] : null;
  };

  // Bunny Stream always serves a `thumbnail.jpg` alongside `playlist.m3u8`
  // for the same video (same base path, just the filename differs). When a
  // Live Channel/Event's video was set by *typing* a Media Library URL
  // directly (see handleUrlChange below), we only have `{ id, HLSPlaylistURL }`
  // — no `thumbnail` field — so the normal `video?.thumbnail` lookup comes up
  // empty. Deriving it straight from the manifest URL means the thumbnail
  // still shows even though nothing was picked through the modal.
  const deriveBunnyThumbnailFromUrl = (url) => {
    if (!url) return null;
    const match = url.match(/^(.*)\/playlist\.m3u8(?:\?.*)?$/i);
    return match ? `${match[1]}/thumbnail.jpg` : null;
  };

  // The URL field starts disabled (read-only display of the current value) —
  // "Edit" enables it, "Cancel" discards whatever was typed and restores the
  // value (and any video/source state it drives) exactly as it was when Edit
  // was clicked.
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const urlBeforeEditRef = useRef("");

  const handleEditUrlClick = () => {
    urlBeforeEditRef.current = formData?.url || "";
    // Clear the field to a blank input rather than editing in place — this
    // goes through handleUrlChange (not a raw clear) so the video preview/
    // selected-media state it drives is dropped too, same as manually
    // clearing the field.
    handleUrlChange({ target: { name: "url", value: "" } });
    setIsEditingUrl(true);
  };

  const handleCancelUrlEdit = () => {
    handleUrlChange({ target: { name: "url", value: urlBeforeEditRef.current } });
    setIsEditingUrl(false);
  };

  const handleUrlChange = (e) => {
    const { value } = e.target;
    handleChange(e);

    if (!value) {
      setVideoLocalPreview(null);
      setVideoFileName(null);
      if (videoFileRef.current) videoFileRef.current.value = "";
      if (onVideoFileChange) onVideoFileChange(null);
      if (setVideo) setVideo(null);
      prevVideoIdRef.current = undefined;
      handleChange({ target: { name: "source_type", value: "url" } });
      return;
    }

    if (!videoLocalPreview && setVideo) {
      const extractedId = extractVideoIdFromUrl(value);
      prevVideoIdRef.current = extractedId || value;
      setVideo({ id: extractedId, HLSPlaylistURL: value });
    }

    handleChange({ target: { name: "source_type", value: "url" } });
  };

  const handleRemoveLogo = (e) => {
    e.stopPropagation();
    setLogoRemoved(true);
    if (onLogoChange) onLogoChange(null);
    if (onLogoRemove) onLogoRemove();   // notify parent
  };

  const handleRemoveHero = (e) => {
    e.stopPropagation();
    setHeroRemoved(true);
    setHeroLocalPreview(null);
    setHeroFileName(null);
    if (onHeroFileChange) onHeroFileChange(null);
    if (onHeroRemove) onHeroRemove();   // notify parent
  };

  const heroSrc = heroRemoved
    ? null
    : heroLocalPreview ||
    (typeof heroPreviewURL === "string" ? heroPreviewURL : null) ||
    heroPreviewURL?.HLSPlaylistURL ||
    heroPreviewURL?.bunny_cdn_url ||
    heroPreviewURL?.preview_url ||
    heroPreviewURL?.thumbnail ||
    heroPreviewURL?.url ||
    video?.preview_url;

  const logoSrc = logoRemoved
    ? null
    : logoPreviewURL?.title_logo_url ||
    logoPreviewURL?.bunny_cdn_url ||
    (typeof logoPreviewURL === "string" ? logoPreviewURL : null);

  const videoSrc =
    videoLocalPreview ||
    video?.HLSPlaylistURL ||
    video?.preview_url ||
    video?.thumbnail ||
    video?.bunny_cdn_url ||
    (typeof video === "string" ? video : null) ||
    formData?.url;
  const liveThumbnailSrc =
    video?.thumbnail ||
    deriveBunnyThumbnailFromUrl(video?.HLSPlaylistURL || formData?.url) ||
    (typeof heroPreviewURL === "string" ? heroPreviewURL : null) ||
    heroPreviewURL?.thumbnail ||
    heroPreviewURL?.bunny_cdn_url ||
    heroPreviewURL?.preview_url ||
    bannerPreviewURL?.bunny_cdn_url ||
    (typeof formData?.landscape === "string" ? formData.landscape : null) ||
    imagePreviewURL?.bunny_cdn_url ||
    (typeof formData?.portrait === "string" ? formData.portrait : null) ||
    null;

  const showSubtitles = isEpisodeAdd || (!isLive && !isSeries && !isSeason);
  const showAudioTracks = !isLive && (!isSeries || isEpisodeAdd) && !isSeason;

  // Does the Main Video section (upload cell + Stream URL/HLS fields) render at all?
  // Now shared by Live Channel / Live Event too — same upload cell + URL field as
  // movies/series, no separate live-only preview UI.
  const showMainVideoSection = (!isSeries || isEpisodeAdd) && !isSeason;
  // Does the Hero Preview section render at all?
  const showHeroSection = !isSeason && !isEpisodeAdd;

  const AUDIO_CODEC_OPTIONS = ["AAC", "AC-3", "E-AC-3", "DTS", "Opus", "MP3"];
  const AUDIO_CHANNEL_OPTIONS = ["1.0", "2.0", "5.1", "7.1"];

  const updateAudioTrack = (id, field, value) => {
    onAudioTracksChange?.(
      audioTracks.map((t) => (t.id === id ? { ...t, [field]: value, ai: false } : t))
    );
  };
  const removeAudioTrack = (id) => {
    onAudioTracksChange?.(audioTracks.filter((t) => t.id !== id));
  };
  const addAudioTrack = () => {
    onAudioTracksChange?.([
      ...audioTracks,
      {
        id: `audio-${Date.now()}`,
        language: "",
        languageCode: "",
        codec: "AAC",
        channels: "2.0",
        label: "",
      },
    ]);
  };

  // Attach a manually-picked audio file to a track row. Sets `file` (uploaded on
  // Save as `audio_tracks`, same path the AI dub uses) plus a local preview and a
  // sensible format from the filename. Not marked `dubbed` — it's a user-supplied
  // track, not AI-generated.
  const attachAudioFile = (id, fileList) => {
    const f = fileList && fileList[0];
    if (!f) return;
    const ext = (f.name.split(".").pop() || "m4a").toLowerCase();
    const previewUrl = URL.createObjectURL(f);
    onAudioTracksChange?.(
      audioTracks.map((t) =>
        t.id === id
          ? { ...t, file: f, format: ext, previewUrl, ai: false }
          : t
      )
    );
  };

  return (
    <Accordion
      title="Media & Assets"
      icon={<img src={`${process.env.PUBLIC_URL}/media.png`} alt="" width={18} height={18} />}
      openId="media"
      {...soloProps}
    >
      <div className={style.mediaAssetsWrapper}>
        {isOpen && (
          <MediaLibraryModal
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            mode={"modal"}
            type={modalMediaType}
            onSelectVideos={setSelectedVideos}
            handleimagesvalue={handleimagesvalue}
            setIsPortraitUploaded={setIsPortraitUploaded}
            showCheckbox={true}
            onSelectImages={(image) => setSelectedImage(image)}
          />
        )}

        {/* ── Main Video — upload cell + Stream URL / HLS Manifest ── */}
        {showMainVideoSection && (
          <div className={style.mediaSection}>
            <div className={style.mediaSectionHeader}>
              <p className={style.mediaSectionTitle}>Main Video</p>
              <p className={style.mediaSectionDesc}>
                Upload your primary video file or add the playback URLs directly.
              </p>
            </div>

            <div className={style.mediaAssetsRow}>
              <button
                className={style.mediaAssetCell}
                aria-label="Upload main content"
                onClick={() => { setModalMediaType("video"); handleIconClick("video"); }}
              >
                <input
                  ref={videoFileRef}
                  type="file"
                  accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-matroska,video/x-msvideo"
                  style={{ display: "none" }}
                  onChange={handleVideoFilePick}
                />
                {isLive ? (
                  // Live Channel / Live Event: thumbnail only, never a playable video.
                  liveThumbnailSrc ? (
                    <div className={style.previewWrapper}>
                      <img
                        src={liveThumbnailSrc}
                        className={style.videoPreview}
                        alt="Live content thumbnail"
                      />
                      <div className={style.iconOverlay}>
                        <IoAdd className={style.overlayIcon} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className={style.mediaAssetTitle}>Main Content File</p>
                      <p className={style.mediaAssetSub}>MP4 · H.264/H.265 master</p>
                    </>
                  )
                ) : videoSrc ? (
                  <div className={style.previewWrapper}>
                    {/* No native controls: the cell is a button that opens the
                        replace picker, so the browser's play button would sit
                        under the + overlay and never actually play. */}
                    <video
                      key={videoSrc}
                      src={videoSrc}
                      // Bunny's HLS manifest (.m3u8) has no native decode in
                      // Chrome/Firefox/Edge without hls.js attached, so without a
                      // poster the element just renders black until Play is
                      // pressed (and even then, only Safari can decode it
                      // directly). Bunny always serves a sibling thumbnail.jpg
                      // next to playlist.m3u8, so use that for the static frame —
                      // same fallback already used for Live content's thumbnail.
                      poster={video?.thumbnail || deriveBunnyThumbnailFromUrl(videoSrc)}
                      className={style.videoPreview}
                      poster={
                        videoLocalPreview
                          ? undefined
                          : video?.thumbnail || deriveBunnyThumbnailFromUrl(videoSrc) || undefined
                      }
                      muted
                      playsInline
                      preload="auto"
                    />
                    <div className={style.iconOverlay}>
                      <IoAdd className={style.overlayIcon} />
                    </div>
                  </div>
                ) : (
                  <>
                    <p className={style.mediaAssetTitle}>Main Content File</p>
                    <p className={style.mediaAssetSub}>MP4 · H.264/H.265 master</p>
                  </>
                )}
              </button>
            </div>

            <div className={style.mediaUrlGroup}>
              <label className={style.mediaUrlLabel}>URL</label>
              <div className={style.mediaUrlEditRow}>
                <input
                  type="text"
                  name="url"
                  value={formData?.url || ""}
                  onChange={handleUrlChange}
                  placeholder="URL"
                  className={style.mediaUrlInput}
                  disabled={!isEditingUrl}
                  readOnly={!isEditingUrl}
                />
                {isEditingUrl ? (
                  <Button
                    type="button"
                    variant="outline"
                    className={style.mediaUrlEditBtn}
                    onClick={handleCancelUrlEdit}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className={style.mediaUrlEditBtn}
                    onClick={handleEditUrlClick}
                  >
                    Edit
                  </Button>
                )}
              </div>
            </div>

            {/* Live-only: keep just the Loop toggle, no separate source-type
                selector or preview player. Shown for every Live main video,
                regardless of how it was added (upload, URL, or Media Library). */}
            {isLive && (
              <div className={style.mediaToggleRow}>
                <span className={style.mediaToggleLabel}>Loop continuously</span>
                <label className={style.mediaToggleTrack}>
                  <input
                    type="checkbox"
                    name="loop"
                    checked={formData?.loop ?? true}
                    onChange={(e) =>
                      handleChange({ target: { name: "loop", value: e.target.checked } })
                    }
                  />
                  <span className={style.mediaToggleSlider} />
                </label>
              </div>
            )}
          </div>
        )}

        {/* ── Artwork — Poster / Backdrop / Logo ── */}
        <div className={style.mediaSection}>
          <div className={style.mediaSectionHeader}>
            <p className={style.mediaSectionTitle}>Artwork</p>
            <p className={style.mediaSectionDesc}>
              Images shown across the storefront, rows and detail pages.
            </p>
            {(isAi("portrait") || isAi("landscape") || isAi("logo")) && (
              <AiGeneratedBadge size="sm" />
            )}
          </div>

          <div className={style.mediaAssetsRow}>
            {/* Poster / Cover Art - ALWAYS VISIBLE */}
            <button
              className={style.mediaAssetCell}
              onClick={() => { setModalMediaType("image"); handleIconClick("portrait"); }}
              aria-label="Upload portrait"
            >
              <input
                ref={portraitInputRef}
                style={{ display: "none" }}
                onChange={handleChange}
                name="portrait"
              />
              {isPortraitUploaded || formData?.portrait ? (
                <div className={style.previewWrapper}>
                  <img
                    src={imagePreviewURL?.bunny_cdn_url || formData?.portrait}
                    className={style.videoPreview}
                  />
                  <div className={style.iconOverlay}>
                    <IoAdd className={style.overlayIcon} />
                  </div>
                </div>
              ) : (
                <>
                  <p className={style.mediaAssetTitle}>Poster / Cover Art</p>
                  <p className={style.mediaAssetSub}>Portrait · 2:3 · JPG/PNG</p>
                </>
              )}
            </button>

            {/* Backdrop Banner */}
            <button
              className={style.mediaAssetCell}
              onClick={() => { setModalMediaType("image"); handleIconClick("landscape"); }}
              aria-label="Upload landscape"
            >
              <input
                style={{ display: "none" }}
                onChange={handleChange}
                name="landscape"
              />
              {isLandscapeUploaded || formData?.landscape ? (
                <div className={style.previewWrapper}>
                  <img
                    src={bannerPreviewURL?.bunny_cdn_url || formData?.landscape}
                    className={style.videoPreview}
                  />
                  <div className={style.iconOverlay}>
                    <IoAdd className={style.overlayIcon} />
                  </div>
                </div>
              ) : (
                <>
                  <p className={style.mediaAssetTitle}>Backdrop Banner</p>
                  <p className={style.mediaAssetSub}>landscape · 16:9 · JPG/PNG</p>
                </>
              )}
            </button>

            {/* Logo / Title Card - HIDDEN IF EPISODE OR SEASON */}
            {!isSeason && !isEpisodeAdd && (
              <div className={style.assetCellWrapper}>
                <button
                  className={style.mediaAssetCell}
                  aria-label="Upload logo"
                  onClick={() => { setModalMediaType("image"); handleIconClick("logo"); }}
                >
                  {logoSrc ? (
                    <div className={style.previewWrapper}>
                      <img
                        src={logoSrc}
                        className={style.videoPreview}
                      />
                      <div className={style.iconOverlay}>
                        <IoAdd className={style.overlayIcon} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className={style.mediaAssetTitle}>Logo / Title Card</p>
                      <p className={style.mediaAssetSub}>PNG with Transparency</p>
                    </>
                  )}
                </button>
                {logoSrc && (
                  <button
                    type="button"
                    className={style.assetRemoveBtn}
                    onClick={handleRemoveLogo}
                  >
                    <IoClose />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Hero Preview ── */}
        {showHeroSection && (
          <div className={style.mediaSection}>
            <div className={style.mediaSectionHeader}>
              <p className={style.mediaSectionTitle}>Hero Preview</p>
              <p className={style.mediaSectionDesc}>
                A short, muted clip that autoplays on featured placements — not the full video.
              </p>
              {isAi("hero") && <AiGeneratedBadge size="sm" />}
            </div>

            {/* heroAssetsRow (not mediaAssetsRow) keeps this row from stretching
                full width; heroAssetCellWrapper gives the cell a fixed, smaller
                size instead of the flex:1 behavior used by the other sections. */}
            <div className={style.heroAssetsRow}>
              <div className={style.heroAssetCellWrapper}>
                <button
                  className={style.mediaAssetCell}
                  aria-label="Upload hero preview clip"
                  onClick={() => { setModalMediaType("video"); handleIconClick("hero"); }}
                >
                  <input
                    ref={heroFileRef}
                    type="file"
                    accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-matroska,video/x-msvideo"
                    style={{ display: "none" }}
                    onChange={handleHeroFilePick}
                  />
                  {heroSrc ? (
                    <div className={style.previewWrapper}>
                      <video
                        key={heroSrc}
                        src={heroSrc}
                        className={style.videoPreview}
                        muted
                        autoPlay
                        loop
                        playsInline
                      />
                      <div className={style.iconOverlay}>
                        <IoAdd className={style.overlayIcon} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className={style.mediaAssetTitle}>Hero Preview Clip</p>
                      <p className={style.mediaAssetSub}>15 - 30s autoplay · MP4</p>
                    </>
                  )}
                </button>
                {heroSrc && (
                  <button
                    type="button"
                    className={style.assetRemoveBtn}
                    onClick={handleRemoveHero}
                  >
                    <IoClose />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Subtitles & Captions ── */}
        {showSubtitles && (
          <>
            <div className={style.mediaToggleRow}>
              <span className={style.mediaToggleLabel}>Subtitles &amp; Captions</span>
              <label className={style.mediaToggleTrack}>
                <input
                  type="checkbox"
                  checked={subtitlesEnabled}
                  onChange={(e) => setSubtitlesEnabled(e.target.checked)}
                />
                <span className={style.mediaToggleSlider} />
              </label>
            </div>

            {/* AI generation only — the manual subtitle upload grid below stays
                available to every client, AI enabled or not. */}
            {aiEnabled && subtitlesEnabled && (
              <div className={style.aiSubtitlePanel}>
                <div className={style.aiSubtitleHeader}>
                  <div>
                    <span className={style.aiSubtitleTitle}>Generate subtitles with AI</span>
                    <p className={style.aiSubtitleHint}>
                      Transcribes the video&apos;s speech into a timed .srt file. Pick extra
                      languages below to also translate it. Review before saving.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleGenerateSubtitles}
                    disabled={aiSubs.isRunning || !aiSubtitleSource}
                  >
                    {aiSubs.isRunning ? "Generating…" : "Generate with AI"}
                  </Button>
                </div>

                {/* Translation language picker. A language that already exists
                    (prefilled or generated) shows a ✓ so returning users see
                    their state at a glance; selecting still lets them regenerate. */}
                <div className={style.aiSubtitleLangs}>
                  {SUBTITLE_TARGET_LANGUAGES.map((l) => {
                    const active = subtitleTargets.includes(l.code);
                    const exists = existingSubtitleLangs.has(l.code);
                    return (
                      <button
                        key={l.code}
                        type="button"
                        className={[
                          style.aiSubtitleLangChip,
                          active ? style.aiSubtitleLangChipActive : "",
                          exists && !active ? style.aiSubtitleLangChipDone : "",
                        ].join(" ")}
                        onClick={() => toggleSubtitleTarget(l.code)}
                        disabled={aiSubs.isRunning}
                        title={exists ? `${l.name} subtitles already added` : `Translate to ${l.name}`}
                      >
                        {exists && <span className={style.aiSubtitleLangCheck}>✓</span>}
                        {l.name}
                      </button>
                    );
                  })}
                </div>
                {subtitleTargets.length > 0 && (
                  <p className={style.aiSubtitleSelectedNote}>
                    Will generate the spoken language
                    {subtitleTargets.length ? ` + ${subtitleTargets.map(langName).join(", ")}` : ""}.
                  </p>
                )}

                {/* Progress / status */}
                {aiSubs.isRunning && (
                  <div className={style.aiSubtitleStatus}>
                    {(aiSubs.stages.find((s) => s.id === aiSubs.activeStage)?.label) || "Working"}
                    … {aiSubs.progress}%
                  </div>
                )}
                {aiSubs.isFailed && (
                  <div className={style.aiSubtitleError}>
                    {aiSubs.error || "Subtitle generation failed."}{" "}
                    <button type="button" className={style.aiSubtitleRetry} onClick={handleGenerateSubtitles}>
                      Retry
                    </button>
                  </div>
                )}
              </div>
            )}

            {subtitlesEnabled && (
              <>
                {/* Hidden file inputs — one per slot, driven by the "+ Add" tile
                    and the per-card replace action. Kept out of the visual grid. */}
                {subtitleFiles.map((_, index) => (
                  <input
                    key={`subtitle-input-${index}`}
                    id={`subtitle-input-${index}`}
                    ref={getSubtitleRef(index)}
                    type="file"
                    accept=".srt"
                    style={{ display: "none" }}
                    onChange={(e) => handleSubtitleFile(index, e)}
                  />
                ))}

                {subtitleCards.length > 0 && (
                  <div className={style.subtitleCountRow}>
                    <MdOutlineSubtitles />
                    <span>
                      {subtitleCards.length} subtitle{subtitleCards.length === 1 ? "" : "s"} ready
                    </span>
                  </div>
                )}

                <div className={style.subtitleCardGrid}>
                  {subtitleCards.map((card) => {
                    const badge = card.aiGenerated
                      ? card.translated
                        ? "AI · Translated"
                        : "AI"
                      : card.kind === "existing"
                      ? "Saved"
                      : "Uploaded";
                    const badgeClass = card.aiGenerated
                      ? style.subtitleBadgeAi
                      : card.kind === "existing"
                      ? style.subtitleBadgeSaved
                      : style.subtitleBadgeUploaded;
                    return (
                      <div key={card.key} className={style.subtitleCard}>
                        <button
                          type="button"
                          className={style.subtitleCardRemove}
                          aria-label="Remove subtitle"
                          onClick={() => {
                            if (card.kind === "existing") {
                              const updated = [...prefillSubtitles];
                              updated.splice(card.prefillIndex, 1);
                              setPrefillSubtitles(updated);
                              if (onSubtitleChange) {
                                onSubtitleChange(buildCombinedSubtitles(updated, subtitleFiles));
                              }
                            } else {
                              removeSubtitleFile(card.fileIndex);
                            }
                          }}
                        >
                          <IoClose />
                        </button>

                        <div className={style.subtitleCardLangBadge}>
                          {(card.language || "??").toUpperCase().slice(0, 2)}
                        </div>
                        <div className={style.subtitleCardBody}>
                          <p className={style.subtitleCardLang}>
                            {card.language ? langName(card.language) : "Subtitle"}
                          </p>
                          <p className={style.subtitleCardMeta}>
                            {(card.format || "srt").toUpperCase()}
                          </p>
                        </div>
                        <div className={style.subtitleCardFoot}>
                          <span className={`${style.subtitleBadge} ${badgeClass}`}>{badge}</span>
                          {card.url && (
                            <a
                              href={card.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={style.subtitleCardView}
                              onClick={(e) => e.stopPropagation()}
                            >
                              View
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Single "add" tile instead of 10 always-visible empty slots. */}
                  {subtitleFiles.some((f) => !f) && (
                    <button
                      type="button"
                      className={style.subtitleAddTile}
                      onClick={openNextSubtitleUpload}
                      aria-label="Add a subtitle file"
                    >
                      <IoAdd className={style.subtitleAddIcon} />
                      <span className={style.subtitleAddLabel}>Add subtitle</span>
                      <span className={style.subtitleAddHint}>Upload .srt</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* ══ Audio Tracks — detected audio languages on the main video ══ */}
        {showAudioTracks && (
          <div className={style.mediaSection}>
            <div className={style.mediaSectionHeader}>
              <p className={style.mediaSectionTitle}>Audio Tracks</p>
              <p className={style.mediaSectionDesc}>
                Audio languages available on the main video. Detected
                automatically — add, edit or remove tracks as needed.
              </p>
              {isAi("audio") && <AiGeneratedBadge size="sm" />}
            </div>

            {/* ── Generate dubbed audio with AI ── */}
            {aiEnabled && (
            <div className={style.aiSubtitlePanel}>
              <div className={style.aiSubtitleHeader}>
                <div>
                  <span className={style.aiSubtitleTitle}>Generate dubbed audio with AI</span>
                  <p className={style.aiSubtitleHint}>
                    Creates a spoken audio track in each chosen language, timed to the
                    video. Uses the video&apos;s dialogue — review before saving.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={handleGenerateDub}
                  disabled={aiDub.isRunning || !aiSubtitleSource || dubTargets.length === 0}
                >
                  {aiDub.isRunning ? "Dubbing…" : "Generate dub"}
                </Button>
              </div>

              <div className={style.aiSubtitleLangs}>
                {SUBTITLE_TARGET_LANGUAGES.map((l) => {
                  const active = dubTargets.includes(l.code);
                  // Match either languageCode or language, so prefilled/existing
                  // tracks are recognized as "already dubbed".
                  const exists = (audioTracks || []).some(
                    (t) =>
                      String(t.languageCode || t.language || "").toLowerCase() === l.code
                  );
                  return (
                    <button
                      key={l.code}
                      type="button"
                      className={[
                        style.aiSubtitleLangChip,
                        active ? style.aiSubtitleLangChipActive : "",
                        exists ? style.aiSubtitleLangChipDone : "",
                      ].join(" ")}
                      // Already-dubbed languages are locked (can't be re-selected),
                      // so a follow-up run only dubs NEW languages — no wasted quota.
                      onClick={() => !exists && toggleDubTarget(l.code)}
                      disabled={aiDub.isRunning || exists}
                      title={exists ? `${l.name} dub already added` : `Dub into ${l.name}`}
                    >
                      {exists && <span className={style.aiSubtitleLangCheck}>✓</span>}
                      {l.name}
                    </button>
                  );
                })}
              </div>

              {aiDub.isRunning && (
                <div className={style.aiSubtitleStatus}>
                  {aiDub.note ||
                    (aiDub.stages.find((s) => s.id === aiDub.activeStage)?.label) ||
                    "Working"}
                  … {aiDub.progress}%
                </div>
              )}
              {aiDub.isFailed && (
                <div className={style.aiSubtitleError}>
                  {aiDub.error || "Dub generation failed."}{" "}
                  <button type="button" className={style.aiSubtitleRetry} onClick={handleGenerateDub}>
                    Retry
                  </button>
                </div>
              )}
            </div>
            )}

            {/* ── Generate seek-bar preview thumbnails (trickplay) with AI ── */}
            {aiEnabled && (
            <div className={style.aiSubtitlePanel}>
              <div className={style.aiSubtitleHeader}>
                <div>
                  <span className={style.aiSubtitleTitle}>
                    Generate scrubbing previews
                    {(thumbnailsVtt || aiThumbs.result?.vttUrl) && (
                      <span className={style.aiSubtitleLangCheck} title="Previews ready"> ✓</span>
                    )}
                  </span>
                  <p className={style.aiSubtitleHint}>
                    Builds the little scene previews shown when a viewer hovers or drags
                    the player&apos;s seek bar. A shorter interval means smoother
                    scrubbing but a larger file and longer generation.
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <select
                    className={style.basicSelect}
                    value={thumbInterval}
                    onChange={(e) => setThumbInterval(Number(e.target.value))}
                    disabled={aiThumbs.isRunning}
                    title="Frame interval — how often a preview frame is captured"
                    style={{ width: "auto", flex: "0 0 auto" }}
                  >
                    <option value={2}>Every 2s (finest)</option>
                    <option value={5}>Every 5s</option>
                    <option value={10}>Every 10s (default)</option>
                    <option value={30}>Every 30s (coarse)</option>
                  </select>
                  <Button
                    type="button"
                    onClick={handleGenerateThumbnails}
                    disabled={aiThumbs.isRunning || !aiSubtitleSource}
                  >
                    {aiThumbs.isRunning ? "Capturing…" : "Generate previews"}
                  </Button>
                </div>
              </div>

              {aiThumbs.isRunning && (
                <div className={style.aiSubtitleStatus}>
                  {aiThumbs.note ||
                    (aiThumbs.stages.find((s) => s.id === aiThumbs.activeStage)?.label) ||
                    "Working"}
                  … {aiThumbs.progress}%
                </div>
              )}
              {aiThumbs.isComplete && aiThumbs.result?.frameCount != null && (
                <div className={style.aiSubtitleStatus}>
                  Ready — {aiThumbs.result.frameCount} preview frame(s). Save to apply.
                </div>
              )}
              {aiThumbs.isFailed && (
                <div className={style.aiSubtitleError}>
                  {aiThumbs.error || "Preview generation failed."}{" "}
                  <button type="button" className={style.aiSubtitleRetry} onClick={handleGenerateThumbnails}>
                    Retry
                  </button>
                </div>
              )}
            </div>
            )}

            {audioTracks.length === 0 ? (
              <p className={style.audioEmptyText}>No audio tracks yet.</p>
            ) : (
              <div className={style.audioTrackList}>
                {audioTracks.map((track) => (
                  <div key={track.id} className={style.audioTrackRow}>
                    <MdOutlineAudiotrack className={style.audioTrackIcon} />
                    <input
                      className={style.audioTrackInput}
                      placeholder="Language (e.g. English)"
                      value={track.language || ""}
                      onChange={(e) =>
                        updateAudioTrack(track.id, "language", e.target.value)
                      }
                    />
                    <select
                      className={style.audioTrackSelect}
                      value={track.codec || ""}
                      onChange={(e) =>
                        updateAudioTrack(track.id, "codec", e.target.value)
                      }
                    >
                      <option value="">Codec</option>
                      {AUDIO_CODEC_OPTIONS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <select
                      className={style.audioTrackSelect}
                      value={track.channels || ""}
                      onChange={(e) =>
                        updateAudioTrack(track.id, "channels", e.target.value)
                      }
                    >
                      <option value="">Channels</option>
                      {AUDIO_CHANNEL_OPTIONS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <input
                      className={style.audioTrackInput}
                      placeholder="Label (e.g. Original)"
                      value={track.label || ""}
                      onChange={(e) =>
                        updateAudioTrack(track.id, "label", e.target.value)
                      }
                    />
                    {track.dubbed && (
                      <span className={`${style.subtitleBadge} ${style.subtitleBadgeAi}`}>
                        AI Dub
                      </span>
                    )}
                    {/* Manual audio-file upload — attaches an .m4a/.mp3 that's uploaded
                        on Save (same path as the AI dub). Hidden native input + label
                        button. Not shown for AI dub rows (those already carry a file). */}
                    {!track.dubbed && (
                      <label
                        className={style.audioTrackUpload}
                        title="Upload an audio file for this track"
                        style={{
                          cursor: "pointer",
                          fontSize: 12,
                          color: "#B87333",
                          border: "1px dashed #B87333",
                          borderRadius: 6,
                          padding: "6px 10px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {track.file ? "Change audio" : "Upload audio"}
                        <input
                          type="file"
                          accept="audio/*,.m4a,.mp3,.aac,.wav"
                          style={{ display: "none" }}
                          onChange={(e) => attachAudioFile(track.id, e.target.files)}
                        />
                      </label>
                    )}
                    {(track.previewUrl || track.url) && (
                      <audio
                        className={style.audioTrackPreview}
                        controls
                        preload="none"
                        src={track.previewUrl || track.url}
                      />
                    )}
                    <button
                      type="button"
                      className={style.audioTrackRemove}
                      onClick={() => removeAudioTrack(track.id)}
                      aria-label="Remove audio track"
                    >
                      <IoClose />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              className={style.audioAddBtn}
              onClick={addAudioTrack}
            >
              <IoAdd /> Add audio track
            </button>
          </div>
        )}
      </div>
    </Accordion>
  );
};

export default MediaAssets;
